
export interface WishlistItem {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  addedAt: string;
}

const WISHLIST_KEY = 'wishlist';

/**
 * Safely parses JSON stored in localStorage.
 * Returns an empty array if parsing fails or data is missing.
 */
const parseWishlist = (raw: string | null): WishlistItem[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    // Ensure the parsed value is an array of objects with required fields
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item) =>
          typeof item.id === 'number' &&
          typeof item.name === 'string' &&
          typeof item.price === 'number' &&
          typeof item.image === 'string' &&
          typeof item.category === 'string' &&
          typeof item.addedAt === 'string'
      );
    }
  } catch (e) {
    console.error('Failed to parse wishlist from localStorage:', e);
  }
  return [];
};

export const getWishlist = (): WishlistItem[] => {
  const data = localStorage.getItem(WISHLIST_KEY);
  return parseWishlist(data);
};

export const isInWishlist = (productId: number): boolean => {
  return getWishlist().some((item) => item.id === productId);
};

export const toggleWishlist = (product: {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
}): boolean => {
  const list = getWishlist();
  const index = list.findIndex((item) => item.id === product.id);

  if (index > -1) {
    // Remove existing item
    list.splice(index, 1);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event('wishlistUpdated'));
    return false;
  }

  // Add new item with timestamp
  list.push({ ...product, addedAt: new Date().toISOString() });
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event('wishlistUpdated'));
  return true;
};

export const removeFromWishlist = (productId: number): void => {
  const list = getWishlist().filter((item) => item.id !== productId);
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event('wishlistUpdated'));
};

export const getWishlistCount = (): number => {
  return getWishlist().length;
};
